import { SearchState } from '../hooks/search/reducers/searchReducer';
import {
    NonProductLeadAnalyticsData,
    ProductLeadAnalyticsData,
    GtmAnalyticsDataEcomCourseItem,
    GtmAnalyticsData,
    GtmAnalyticsleadFormSubmissionError,
} from '../models/globals';
import { getMarketingCategory, getMarketingVariant } from '../utilities/ProductUtilities';

const CONTAINER_ID = 'GTM-FAKE_ID';

const publishAnalyticsEvent = ({ containerId, message }: GtmAnalyticsData): Promise<void> => {
    return new Promise((resolve, reject) => {
        if (!window.google_tag_manager || window.ga) {
            return reject(
                new Error(
                    'Skipping dataLayer push: Google Analytics and/or Tag Manager may be adblocked or may have failed to load',
                ),
            );
        }

        const eventTimeout = 2000;

        const timeoutId = setTimeout(() => {
            reject(new Error('event callback never fired with correct containerId'));
        }, eventTimeout + 100);

        window.dataLayer = window.dataLayer || [];

        window.dataLayer.push({
            ...message,
            eventCallback(id: string) {
                if (id === containerId) {
                    clearTimeout(timeoutId);
                    resolve();
                }
            },
            eventTimeout,
        });
    });
};

async function publishProductClickAnalyticsEvent<
    T extends {
        vendorName: string;
        code: string;
        title: string;
        classification: unknown;
        isVoucherIncluded: boolean;
        type: unknown;
        categoryName: string;
        subCategoryName: string;
        product?: Record<string, unknown>;
    },
>(product: T, index: number, wholesalePriceModalCurrencyCode?: string | undefined): Promise<void> {
    const message = {
        event: 'selectItem',
        web_source: 'portal',
        wholesalePriceModalCurrencyCode,
        ecommerce: {
            click: {
                products: [
                    {
                        name: product.title,
                        id: product.code,
                        brand: product.vendorName,
                        category: getMarketingCategory(product),
                        variant: getMarketingVariant(product),
                        position: index + 1,
                    },
                ],
            },
        },
    };

    return publishAnalyticsEvent({ containerId: CONTAINER_ID, message });
}

const isEmailOptinChecked = () => {
    const emailOptinCheckbox = document.getElementById('formEmailOptIn') as HTMLInputElement;

    return emailOptinCheckbox.checked;
};

const publishLeadFormSubmissionEvent = (
    analyticsData: ProductLeadAnalyticsData | NonProductLeadAnalyticsData | undefined,
    leadFormAnalyticsData: GtmAnalyticsDataEcomCourseItem[] | undefined,
    wholesalePriceModalCurrencyCode?: string | undefined,
) => {
    const message = {
        ...analyticsData,
        form_location: window.location.href,
        email_optin: isEmailOptinChecked(),
        course: leadFormAnalyticsData,
        wholesalePriceModalCurrencyCode,
    };

    publishAnalyticsEvent({ containerId: CONTAINER_ID, message });
};

const publishLeadFormSubmissionErrorEvent = (
    leadFormSubmissionErrorAnalyticsData: GtmAnalyticsleadFormSubmissionError,
    wholesalePriceModalCurrencyCode?: string | undefined,
) => {
    const message = {
        event: 'Lead_Form_Submission',
        ...leadFormSubmissionErrorAnalyticsData,
        wholesalePriceModalCurrencyCode,
    };

    publishAnalyticsEvent({ containerId: CONTAINER_ID, message });
};

const publishNoSearchResult = ({
    courseTypes,
    page,
    pageSize,
    searchTerms,
    sortInUse,
    subTopics,
    topics,
    wholesalePriceModalCurrencyCode,
}: SearchState): Promise<void> => {
    const message = {
        event: 'noResult',
        course_types: Array.from(courseTypes),
        page_number: page,
        page_size: pageSize,
        search_term: searchTerms,
        sort: sortInUse,
        sub_topics: Array.from(subTopics),
        topics: Array.from(topics),
        web_source: 'portal',
        wholesalePriceModalCurrencyCode,
    };

    return publishAnalyticsEvent({ containerId: CONTAINER_ID, message });
};

async function* publishSearchProductImpressionEvents(
    {
        courseTypes,
        page: endPage,
        pageSize,
        searchTerms,
        visibleProductTiles,
        sortInUse,
        subTopics,
        topics,
        wholesalePriceModalCurrencyCode,
    }: SearchState,
    startPage: number,
): AsyncGenerator<void, void, void> {
    const commonMessage = {
        event: 'productImpression',
        course_types: Array.from(courseTypes),
        page_size: pageSize,
        search_term: searchTerms,
        sort: sortInUse,
        sub_topics: Array.from(subTopics),
        topics: Array.from(topics),
        web_source: 'portal',
    };

    while (startPage <= endPage) {
        const startIndex = (startPage - 1) * pageSize;
        const startPosition = startIndex + 1;
        if (visibleProductTiles.length < startPosition) break;
        const endIndex = startPage * pageSize;
        const message = {
            ...commonMessage,
            page_number: startPage,
            ecommerce: {
                wholesalePriceModalCurrencyCode,
                impressions: visibleProductTiles.slice(startIndex, endIndex).map((product, index) => ({
                    brand: product.vendorName,
                    category: getMarketingCategory(product),
                    id: product.code,
                    list: 'Search Results',
                    name: product.title,
                    position: index + startPosition,
                    variant: getMarketingVariant(product),
                })),
            },
        };

        yield await publishAnalyticsEvent({ containerId: CONTAINER_ID, message });

        startPage++;
    }
}

const publishOptimizelyExperimentDataEvent = (wholesalePriceModalCurrencyCode?: string | undefined) => {
    setTimeout(() => {
        const state = (window as any).optimizely?.get('state');
        const experiments = state.getExperimentStates({ isActive: true });
        const experimentKeys = Object.keys(experiments);
        experimentKeys.forEach((key) => {
            const exp = experiments[key];

            const message = {
                event: 'Optimizely_Experiment_Data',
                experiment_id: key,
                experiment_name: exp.experimentName || 'unknown',
                variant_name: exp.variation?.name || 'unknown',
                wholesalePriceModalCurrencyCode,
            };

            publishAnalyticsEvent({ containerId: CONTAINER_ID, message });
        });
    }, 3000);
};
// Simple debounce helper
function debounce<F extends (...args: any[]) => void>(func: F, wait: number) {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    return (...args: Parameters<F>) => {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
}
function getFormattedTimestamp() {
    const now = new Date();

    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();

    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
}
function pushAccountDropdownEvent(option: string, clickUrl: string, learnerGuid: string) {
    if (!clickUrl || clickUrl.trim() === '') {
        clickUrl = window.location.href;
    }

    try {
        // Wrap payload inside `message` like your noResult example
        const analyticsPayload = {
            containerId: CONTAINER_ID, // Choose a unique containerId
            message: {
                event: 'accountDropdownClick',
                learner_identifier: learnerGuid,
                option: option,
                click_url: clickUrl,
                site_type: 'Portal',
                timestamp: getFormattedTimestamp(),
            },
        };

        // Correct way to call publishAnalyticsEvent
        publishAnalyticsEvent(analyticsPayload).catch(console.error);
        console.log('Analytics event pushed:', analyticsPayload.message);
    } catch (err) {
        console.error('Error in pushAccountDropdownEvent:', err);
    }

    return true;
}
export {
    publishAnalyticsEvent,
    publishProductClickAnalyticsEvent,
    publishLeadFormSubmissionEvent,
    publishLeadFormSubmissionErrorEvent,
    publishNoSearchResult,
    publishSearchProductImpressionEvents,
    publishOptimizelyExperimentDataEvent,
    pushAccountDropdownEvent,
    debounce,
};
